import React, { useState, useEffect, useMemo } from 'react';
import { biocideService } from '../../services/biocideService';
import authService from '../../services/authService';

const STATUS_COLORS = {
  activ: { bg: '#d1fae5', color: '#065f46' },
  expirat: { bg: '#fee2e2', color: '#991b1b' },
  necunoscut: { bg: '#f3f4f6', color: '#6b7280' }
};

const TIP_PRODUS_LABELS = {
  1: 'Igienă umană',
  2: 'Dezinfectante suprafețe',
  3: 'Igienă veterinară',
  4: 'Produse alimentare',
  5: 'Apă potabilă',
  6: 'Conservanți depozitare',
  7: 'Conservanți pelicule',
  8: 'Conservanți lemn',
  9: 'Conservanți fibre',
  10: 'Conservanți construcții',
  11: 'Sisteme răcire',
  12: 'Slimicide',
  13: 'Fluide prelucrare',
  14: 'Rodenticide',
  15: 'Avicide',
  16: 'Moluscocide',
  17: 'Piscicide',
  18: 'Insecticide/acaricide',
  19: 'Repelenți',
  20: 'Vertebrate',
  21: 'Antivegetative',
  22: 'Îmbălsămare'
};

const SPAULDING_OPTIONS = [
  { value: '', label: '— Fără clasificare —' },
  {
    value: 'sterilizant',
    label: 'Sterilizant pentru obiecte critice'
  },
  {
    value: 'hld',
    label: 'Dezinfectant de nivel înalt (obiecte semi-critice)'
  },
  {
    value: 'ild',
    label: 'Dezinfectant de nivel intermediar (obiecte semi-critice sau necritice)'
  },
  {
    value: 'lld',
    label: 'Dezinfectant de nivel scăzut sau detergent (obiecte necritice)'
  }
];

const SPAULDING_COLORS = {
  sterilizant: { bg: '#fce7f3', color: '#9d174d' },
  hld: { bg: '#fee2e2', color: '#991b1b' },
  ild: { bg: '#fef3c7', color: '#92400e' },
  lld: { bg: '#d1fae5', color: '#065f46' }
};

/** Valoare filtru: rânduri fără `clasificareSpaulding` (null / lipsă / gol). */
const FILTER_CLASIFICARE_GOALE = '__fara_clasificare__';

function tipDisplayLabels(item) {
  const fromDoc = item.tipProdusLabel?.filter(Boolean);
  if (fromDoc?.length) return fromDoc;
  const codes = item.tipProdus || [];
  return codes.map((c) => TIP_PRODUS_LABELS[c] || `Tip ${c}`);
}

const BiocideList = () => {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTipProdus, setFilterTipProdus] = useState('');
  const [filterClasificare, setFilterClasificare] = useState('');
  const [canMutate, setCanMutate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedSubstante, setExpandedSubstante] = useState({}); // { [itemId]: boolean }
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    authService.getCurrentUserProfile().then((p) => {
      setCanMutate(['management', 'ic_personnel'].includes(p?.role_id));
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { items } = await biocideService.getBiocide();
        if (!cancelled) setAllRows(items);
      } catch (err) {
        if (!cancelled) {
          setError('Eroare la încărcarea biocidelor.');
          console.error(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredItems = useMemo(() => {
    let rows = allRows;
    if (filterStatus) rows = rows.filter((b) => b.status === filterStatus);
    if (filterTipProdus !== '') {
      const code = Number(filterTipProdus);
      rows = rows.filter(
        (b) =>
          Array.isArray(b.tipProdus) &&
          b.tipProdus.some((c) => Number(c) === code)
      );
    }
    if (filterClasificare === FILTER_CLASIFICARE_GOALE) {
      rows = rows.filter((b) => !b.clasificareSpaulding || b.clasificareSpaulding === '');
    } else if (filterClasificare !== '') {
      rows = rows.filter((b) => b.clasificareSpaulding === filterClasificare);
    }
    const term = debouncedSearch.trim().toLowerCase();
    if (term) {
      rows = rows.filter(
        (b) =>
          b.denumireComerciale?.some((d) => d.toLowerCase().includes(term)) ||
          (b.producator && b.producator.toLowerCase().includes(term)) ||
          (b.nrAviz && String(b.nrAviz).toLowerCase().includes(term)) ||
          b.substanteActive?.some((s) => s.denumire && s.denumire.toLowerCase().includes(term))
      );
    }
    return rows;
  }, [allRows, debouncedSearch, filterStatus, filterTipProdus, filterClasificare]);

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditData({
      nrAviz: item.nrAviz || '',
      denumirePrincipala: item.denumireComerciale?.[0] || '',
      producator: item.producator || '',
      taraProducator: item.taraProducator || '',
      status: item.status || 'necunoscut',
      substanteActive: item.substanteActive ? item.substanteActive.map((s) => ({ ...s })) : [],
      clasificareSpaulding: item.clasificareSpaulding || ''
    });
    setDeletingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async (item) => {
    setSavingId(item.id);
    try {
      const currentUser = authService.getCurrentUser();
      const userId = currentUser?.uid || 'system';
      const newDenumiri = [...(item.denumireComerciale || [])];
      newDenumiri[0] = editData.denumirePrincipala;
      const clasificareSpaulding =
        editData.clasificareSpaulding === '' ? null : editData.clasificareSpaulding;

      await biocideService.updateBiocid(
        item.id,
        {
          nrAviz: editData.nrAviz,
          denumireComerciale: newDenumiri,
          producator: editData.producator,
          taraProducator: editData.taraProducator,
          status: editData.status,
          substanteActive: editData.substanteActive || [],
          clasificareSpaulding
        },
        userId
      );

      setAllRows((prev) =>
        prev.map((i) =>
          i.id === item.id
            ? {
                ...i,
                nrAviz: editData.nrAviz,
                denumireComerciale: newDenumiri,
                producator: editData.producator,
                taraProducator: editData.taraProducator,
                status: editData.status,
                substanteActive: editData.substanteActive || [],
                clasificareSpaulding
              }
            : i
        )
      );
      setEditingId(null);
      setEditData({});
    } catch (err) {
      setError('Eroare la salvarea modificărilor.');
      console.error(err);
    } finally {
      setSavingId(null);
    }
  };

  const confirmDelete = (id) => {
    setDeletingId(id);
    setEditingId(null);
  };

  const cancelDelete = () => setDeletingId(null);

  const executeDelete = async (id) => {
    try {
      await biocideService.deleteBiocid(id);
      setAllRows((prev) => prev.filter((i) => i.id !== id));
      setDeletingId(null);
    } catch (err) {
      setError('Eroare la ștergerea biocidului.');
      console.error(err);
    }
  };

  return (
    <div className="content-card">
      <div className="card-header">
        <div className="card-title">Registrul Național al Produselor Biocide</div>
        <div style={{ fontSize: '12px', color: '#6b7280' }}>Total: {allRows.length}</div>
      </div>

      <div className="card-content">
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Caută după denumire, producător, nr. aviz..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              minWidth: '200px',
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            <option value="">Toate statusurile</option>
            <option value="activ">Activ</option>
            <option value="expirat">Expirat</option>
            <option value="necunoscut">Necunoscut</option>
          </select>
          <select
            value={filterTipProdus}
            onChange={(e) => setFilterTipProdus(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px',
              minWidth: '220px',
              maxWidth: '100%'
            }}
          >
            <option value="">Toate tipurile de produs</option>
            {Object.entries(TIP_PRODUS_LABELS).map(([code, label]) => (
              <option key={code} value={code}>
                {code}. {label}
              </option>
            ))}
          </select>
          <select
            value={filterClasificare}
            onChange={(e) => setFilterClasificare(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px',
              minWidth: '220px',
              maxWidth: '100%'
            }}
          >
            <option value="">Toate clasificările</option>
            <option value={FILTER_CLASIFICARE_GOALE}>Fără clasificare (nemapat)</option>
            {SPAULDING_OPTIONS.filter((o) => o.value).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {!canMutate && !loading && allRows.length > 0 && (
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
            Modificarea registrului (editare / ștergere) este disponibilă pentru management și personal IC.
          </div>
        )}

        {error && (
          <div
            style={{
              padding: '8px 12px',
              marginBottom: '12px',
              backgroundColor: '#fee2e2',
              color: '#991b1b',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          >
            {error}
            <button
              type="button"
              onClick={() => setError(null)}
              style={{
                marginLeft: '8px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#991b1b'
              }}
            >
              ✕
            </button>
          </div>
        )}

        {loading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>
            Se încarcă biocidele...
          </div>
        ) : allRows.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>
            Nu există biocide înregistrate. Rulați <code>node scripts/seed-biocide.js</code> pentru a popula
            registrul.
          </div>
        ) : filteredItems.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#6b7280' }}>
            Niciun rezultat pentru filtrele curente.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                  <th style={thStyle}>Nr. Aviz</th>
                  <th style={thStyle}>Denumire</th>
                  <th style={thStyle}>Producător</th>
                  <th style={thStyle}>Tip produs</th>
                  <th style={thStyle}>Clasificare</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, width: canMutate ? '140px' : '80px' }}>Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <React.Fragment key={item.id}>
                    <tr
                      style={{
                        borderBottom: '1px solid #e2e8f0',
                        backgroundColor:
                          editingId === item.id ? '#f0f8ff' : deletingId === item.id ? '#fff5f5' : '#ffffff'
                      }}
                    >
                      <td style={tdStyle}>
                        {editingId === item.id ? (
                          <input
                            value={editData.nrAviz}
                            onChange={(e) => setEditData((p) => ({ ...p, nrAviz: e.target.value }))}
                            style={inputStyle}
                          />
                        ) : (
                          <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{item.nrAviz || '—'}</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {editingId === item.id ? (
                          <div>
                            <input
                              value={editData.denumirePrincipala}
                              onChange={(e) => setEditData((p) => ({ ...p, denumirePrincipala: e.target.value }))}
                              style={inputStyle}
                            />
                            {editData.substanteActive?.length > 0 && (
                              <div style={{ marginTop: '6px' }}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedSubstante((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
                                  }}
                                  style={{
                                    fontSize: '11px',
                                    color: '#6366f1',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0
                                  }}
                                >
                                  {expandedSubstante[item.id] ? '▲' : '▼'}{' '}
                                  {editData.substanteActive.length} substanță/e active
                                </button>
                                {expandedSubstante[item.id] && (
                                  <div style={{ marginTop: '4px' }}>
                                    {editData.substanteActive.map((s, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          display: 'grid',
                                          gridTemplateColumns: '2fr 1fr 1fr 1fr',
                                          gap: '4px',
                                          marginBottom: '4px'
                                        }}
                                      >
                                        <input
                                          value={s.denumire || ''}
                                          onChange={(e) => {
                                            const updated = [...editData.substanteActive];
                                            updated[i] = { ...updated[i], denumire: e.target.value };
                                            setEditData((p) => ({ ...p, substanteActive: updated }));
                                          }}
                                          style={inputStyle}
                                          placeholder="Denumire substanță"
                                        />
                                        <input
                                          value={s.concentratie || ''}
                                          onChange={(e) => {
                                            const updated = [...editData.substanteActive];
                                            updated[i] = { ...updated[i], concentratie: e.target.value };
                                            setEditData((p) => ({ ...p, substanteActive: updated }));
                                          }}
                                          style={inputStyle}
                                          placeholder="Conc."
                                        />
                                        <input
                                          value={s.numarCE || ''}
                                          onChange={(e) => {
                                            const updated = [...editData.substanteActive];
                                            updated[i] = { ...updated[i], numarCE: e.target.value };
                                            setEditData((p) => ({ ...p, substanteActive: updated }));
                                          }}
                                          style={inputStyle}
                                          placeholder="Nr. CE"
                                        />
                                        <input
                                          value={s.numarCAS || ''}
                                          onChange={(e) => {
                                            const updated = [...editData.substanteActive];
                                            updated[i] = { ...updated[i], numarCAS: e.target.value };
                                            setEditData((p) => ({ ...p, substanteActive: updated }));
                                          }}
                                          style={inputStyle}
                                          placeholder="Nr. CAS"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '6px' }}>
                              msId {item.msId ?? '—'} (read-only) · scraped {item.scrapedAt ? String(item.scrapedAt).slice(0, 10) : '—'}
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div style={{ fontWeight: '500' }}>{item.denumireComerciale?.[0] || '—'}</div>
                            {item.denumireComerciale?.length > 1 && (
                              <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                +{item.denumireComerciale.length - 1} alias-uri
                              </div>
                            )}
                            {item.substanteActive?.length > 0 && (
                              <div style={{ marginTop: '4px' }}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedSubstante((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
                                  }}
                                  style={{
                                    fontSize: '11px',
                                    color: '#6366f1',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0
                                  }}
                                >
                                  {expandedSubstante[item.id] ? '▲' : '▼'} {item.substanteActive.length} substanță/e active
                                </button>
                                {expandedSubstante[item.id] && (
                                  <div style={{ marginTop: '4px', fontSize: '11px', color: '#4b5563' }}>
                                    {item.substanteActive.map((s, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          padding: '2px 0',
                                          borderTop: i > 0 ? '1px solid #f3f4f6' : 'none'
                                        }}
                                      >
                                        <span style={{ fontWeight: '500' }}>{s.denumire || '—'}</span>
                                        {s.concentratie && (
                                          <span style={{ marginLeft: '6px', color: '#6b7280' }}>{s.concentratie}</span>
                                        )}
                                        {s.numarCE && (
                                          <span style={{ marginLeft: '6px', color: '#9ca3af' }}>CE: {s.numarCE}</span>
                                        )}
                                        {s.numarCAS && (
                                          <span style={{ marginLeft: '6px', color: '#9ca3af' }}>CAS: {s.numarCAS}</span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {editingId === item.id ? (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <input
                              value={editData.producator}
                              onChange={(e) => setEditData((p) => ({ ...p, producator: e.target.value }))}
                              style={{ ...inputStyle, flex: 2 }}
                              placeholder="Producător"
                            />
                            <input
                              value={editData.taraProducator}
                              onChange={(e) => setEditData((p) => ({ ...p, taraProducator: e.target.value }))}
                              style={{ ...inputStyle, flex: 1 }}
                              placeholder="Țară"
                            />
                          </div>
                        ) : (
                          <span>
                            {item.producator || '—'}
                            {item.taraProducator ? ` (${item.taraProducator})` : ''}
                          </span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {tipDisplayLabels(item).map((label, i) => (
                          <span
                            key={i}
                            style={{
                              display: 'inline-block',
                              marginRight: '4px',
                              marginBottom: '2px',
                              padding: '1px 6px',
                              borderRadius: '8px',
                              fontSize: '10px',
                              backgroundColor: '#e0e7ff',
                              color: '#3730a3'
                            }}
                          >
                            {label}
                          </span>
                        ))}
                      </td>
                      <td style={tdStyle}>
                        {editingId === item.id && canMutate ? (
                          <select
                            value={editData.clasificareSpaulding || ''}
                            onChange={(e) =>
                              setEditData((p) => ({ ...p, clasificareSpaulding: e.target.value }))
                            }
                            style={inputStyle}
                          >
                            {SPAULDING_OPTIONS.map((o) => (
                              <option key={o.value === '' ? '_empty' : o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        ) : item.clasificareSpaulding ? (
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: '500',
                              display: 'inline-block',
                              maxWidth: 'min(100%, 320px)',
                              whiteSpace: 'normal',
                              lineHeight: 1.35,
                              backgroundColor: SPAULDING_COLORS[item.clasificareSpaulding]?.bg || '#f3f4f6',
                              color: SPAULDING_COLORS[item.clasificareSpaulding]?.color || '#6b7280'
                            }}
                          >
                            {SPAULDING_OPTIONS.find((o) => o.value && o.value === item.clasificareSpaulding)
                              ?.label || item.clasificareSpaulding}
                          </span>
                        ) : (
                          <span style={{ fontSize: '11px', color: '#d1d5db' }}>—</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {editingId === item.id ? (
                          <select
                            value={editData.status}
                            onChange={(e) => setEditData((p) => ({ ...p, status: e.target.value }))}
                            style={inputStyle}
                          >
                            <option value="activ">activ</option>
                            <option value="expirat">expirat</option>
                            <option value="necunoscut">necunoscut</option>
                          </select>
                        ) : (
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: '8px',
                              fontSize: '11px',
                              fontWeight: '500',
                              backgroundColor: STATUS_COLORS[item.status]?.bg || '#f3f4f6',
                              color: STATUS_COLORS[item.status]?.color || '#6b7280'
                            }}
                          >
                            {item.status || 'necunoscut'}
                          </span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                        {!canMutate ? (
                          <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
                        ) : deletingId === item.id ? null : editingId === item.id ? (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              type="button"
                              onClick={() => saveEdit(item)}
                              disabled={savingId === item.id}
                              style={{ ...btnStyle, backgroundColor: '#16a34a', color: '#fff' }}
                            >
                              {savingId === item.id ? '...' : 'Salvează'}
                            </button>
                            <button type="button" onClick={cancelEdit} style={{ ...btnStyle, backgroundColor: '#6b7280', color: '#fff' }}>
                              Anulează
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button type="button" onClick={() => startEdit(item)} style={{ ...btnStyle, backgroundColor: '#3b82f6', color: '#fff' }}>
                              Editează
                            </button>
                            <button type="button" onClick={() => confirmDelete(item.id)} style={{ ...btnStyle, backgroundColor: '#ef4444', color: '#fff' }}>
                              Șterge
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {canMutate && deletingId === item.id && (
                      <tr style={{ backgroundColor: '#fff5f5' }}>
                        <td colSpan={7} style={{ padding: '8px 12px', borderBottom: '1px solid #fca5a5' }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              fontSize: '13px',
                              color: '#991b1b',
                              flexWrap: 'wrap'
                            }}
                          >
                            <span>
                              Confirmi ștergerea biocidului{' '}
                              <strong>{item.denumireComerciale?.[0] || item.nrAviz || item.id}</strong>? Această acțiune
                              nu poate fi anulată.
                            </span>
                            <button type="button" onClick={() => executeDelete(item.id)} style={{ ...btnStyle, backgroundColor: '#dc2626', color: '#fff' }}>
                              Confirmă ștergerea
                            </button>
                            <button type="button" onClick={cancelDelete} style={{ ...btnStyle, backgroundColor: '#6b7280', color: '#fff' }}>
                              Anulează
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: '600',
  color: '#374151',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const tdStyle = {
  padding: '10px 12px',
  verticalAlign: 'middle',
  color: '#374151'
};

const inputStyle = {
  width: '100%',
  padding: '4px 8px',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  fontSize: '13px'
};

const btnStyle = {
  padding: '4px 10px',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: '500'
};

export default BiocideList;
